import "../css/app.css";
import "./bootstrap";
import { initializeAssetConfig } from "./lib/asset-config";
import { formatBrowserTitle } from "./lib/browser-title";
import { setupInertiaTracking } from "./lib/inertia-sentry";
import { initSentry } from "./lib/sentry";
import { createInertiaApp } from "@inertiajs/react";
import * as Sentry from "@sentry/react";
import { resolvePageComponent } from "laravel-vite-plugin/inertia-helpers";
import { createRoot, hydrateRoot } from "react-dom/client";

interface AppPageProps {
  app?: {
    assetDomainOverride?: string;
  };
}

// Development console logger - only loads when VITE_DEBUG=true
if (import.meta.env.DEV && import.meta.env.VITE_DEBUG === "true") {
  import("./lib/console-logger");
}

// Initialize Sentry before creating the app
initSentry();

// Setup Inertia tracking after Sentry is initialized
setupInertiaTracking();

createInertiaApp({
  title: (title) => formatBrowserTitle(title),
  resolve: (name) =>
    resolvePageComponent(
      `./App/Pages/${name}.tsx`,
      // Only include Public pages and their minimal dependencies
      import.meta.glob([
        // Public pages
        "./App/Pages/Public/**/*.tsx",

        // Auth pages
        "./App/Pages/Auth/**/*.tsx",

        // Rendered by ExceptionResponder when a guest route fails
        "./App/Pages/Error.tsx",

        // Layouts used by public and auth pages
        "./App/Layouts/Unauthenticated*.tsx",
        "./App/Layouts/GuestLayout.tsx",
        "./App/Layouts/NeoLogo.tsx",

        // UI components used by public and auth pages
        "./Shared/Components/ui/button.tsx",
        "./Shared/Components/RotatingBackground.tsx",
        "./Shared/Components/InputError.tsx",
        "./Shared/Components/InputLabel.tsx",
        "./Shared/Components/PrimaryButton.tsx",
        "./Shared/Components/TextInput.tsx",
        "./Shared/Components/Icons/NeoLogo.tsx",

        // Hooks and utilities
        "./hooks/useInviteRequest.ts",
        "./lib/utils.ts",
        "./lib/asset-config.ts",

        // Types
        "./types/index.ts",
      ]),
    ),
  setup({ el, App, props }) {
    // Initialize asset domain override from page props
    initializeAssetConfig(
      (props.initialPage.props as AppPageProps).app?.assetDomainOverride,
    );

    // Wrap the App component with Sentry error boundary
    const SentryApp = Sentry.withErrorBoundary(App, {
      fallback: ({ resetError }) => {
        return (
          <div className="flex min-h-screen items-center justify-center">
            <div className="text-center">
              <h2 className="mb-2 text-xl font-semibold">
                Something went wrong
              </h2>
              <p className="mb-4 text-gray-600">
                An error occurred while loading the page.
              </p>
              <div className="space-x-4">
                <button
                  onClick={resetError}
                  className="rounded bg-blue-600 px-4 py-2 text-white hover:bg-blue-700"
                >
                  Try again
                </button>
              </div>
            </div>
          </div>
        );
      },
      showDialog: import.meta.env.DEV,
    });

    if (import.meta.env.SSR) {
      hydrateRoot(el, <SentryApp {...props} />);
      return;
    }

    createRoot(el).render(<SentryApp {...props} />);
  },
  progress: {
    color: "#4B5563",
  },
});
